import type { SearchProduct, SearchProductCustomField, SearchProductSku } from "@nosto/nosto-js/client"

/**
 * Reads every value that a product carries for a given facet field. The same
 * helper has to cope with:
 *  - top-level product fields (e.g. `brand`, `productType`)
 *  - product-level `customFields` keyed entries (`customFields.attributes-fit`)
 *  - variant-level SKU customFields (`skus.customFields.size`) — Nosto exposes
 *    fields like Size / Inseam via the per-SKU customFields array, so the
 *    facet `field` resolves down into `skus[].customFields[]` rather than the
 *    product's own customFields. Without this branch, those facets fell back
 *    to Nosto's raw counts (which are per-SKU, not per-product), producing
 *    inflated chip totals like "Size 30 (361)" on a 395-product PLP.
 *
 * Some Shopify metafields land in Nosto as JSON-stringified arrays — those are
 * parsed and flattened so each entry becomes its own searchable value.
 *
 * Values are de-duplicated per product so a product whose SKUs collectively
 * cover the same size multiple times only counts once.
 */
const CUSTOM_FIELD_PREFIX = "customFields."
const SKU_CUSTOM_FIELD_PREFIX = "skus.customFields."
const SKU_PREFIX = "skus."

export function readProductFieldValues(
  product: SearchProduct,
  field: string | undefined
): string[] {
  if (!field) return []

  // Variant-level customFields: e.g. `skus.customFields.size`.
  if (field.startsWith(SKU_CUSTOM_FIELD_PREFIX)) {
    const key = field.slice(SKU_CUSTOM_FIELD_PREFIX.length)
    return collectFromSkus(product.skus, sku =>
      (sku.customFields ?? [])
        .filter((c: SearchProductCustomField) => c.key === key)
        .flatMap((c: SearchProductCustomField) => splitValue(c.value))
    )
  }

  // Top-level SKU fields: e.g. `skus.price`. Rare but supported for symmetry.
  if (field.startsWith(SKU_PREFIX)) {
    const key = field.slice(SKU_PREFIX.length)
    return collectFromSkus(product.skus, sku => {
      const raw = (sku as unknown as Record<string, unknown>)[key]
      if (typeof raw === "string") return splitValue(raw)
      if (Array.isArray(raw)) return raw.flatMap(splitValue)
      return []
    })
  }

  // Nosto facet `field` for product-level metafields is `customFields.<key>`,
  // while each `SearchProductCustomField` carries the bare `<key>` (e.g.
  // `attributes-fit`). Strip the prefix before matching against the array.
  const customKey = field.startsWith(CUSTOM_FIELD_PREFIX)
    ? field.slice(CUSTOM_FIELD_PREFIX.length)
    : field

  const customMatches = (product.customFields ?? [])
    .filter((c: SearchProductCustomField) => c.key === customKey)
    .flatMap((c: SearchProductCustomField) => splitValue(c.value))

  if (customMatches.length > 0) {
    return customMatches
  }

  // Fall back to top-level fields (brand, productType, gender, tags1, …).
  const raw = (product as unknown as Record<string, unknown>)[field]
  if (typeof raw === "string") return splitValue(raw)
  if (Array.isArray(raw)) return raw.flatMap(splitValue)
  return []
}

function collectFromSkus(
  skus: readonly SearchProductSku[] | undefined,
  extract: (sku: SearchProductSku) => string[]
): string[] {
  if (!skus || skus.length === 0) return []
  const seen = new Set<string>()
  for (const sku of skus) {
    for (const value of extract(sku)) {
      if (value) seen.add(value)
    }
  }
  return [...seen]
}

function splitValue(value: unknown): string[] {
  if (typeof value !== "string") return []
  const trimmed = value.trim()
  if (!trimmed) return []
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.filter(v => typeof v === "string").map(v => v.trim()).filter(Boolean)
      }
    } catch {
      // fall through
    }
  }
  return [trimmed]
}

/**
 * Counts how many `hits` carry each value for the given facet field. Used to
 * override Nosto's raw counts with deduped-aware counts so the chip totals
 * match the rendered grid.
 *
 * Returns a `Map` keyed by lowercased value (so casing duplicates such as
 * "Baggy" / "baggy" both contribute to the same bucket) and stores the count
 * alongside the canonical-cased values that contributed to it.
 */
export function countFacetValuesAcrossHits(
  hits: readonly SearchProduct[],
  field: string | undefined
): Map<string, number> {
  const counts = new Map<string, number>()
  if (!field) return counts

  for (const hit of hits) {
    const seenForHit = new Set<string>()
    for (const value of readProductFieldValues(hit, field)) {
      const key = value.toLowerCase()
      if (seenForHit.has(key)) continue
      seenForHit.add(key)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }

  return counts
}

type StyleFamilyHit = SearchProduct & { styleFamilyKey?: string }

/**
 * Counts each value as "number of *style families* that contain at least one
 * variant with this value", instead of "number of representative products with
 * this value".
 *
 * Why: `dedupeByStyleFamily` only keeps ONE Nosto hit per `styleFamilyKey`
 * (the first one ranked) — that representative carries a single Color/Size
 * value. Counting from deduped hits therefore under-reports availability for
 * the family's *non-representative* variants. When a user then selects e.g.
 * "Color: Black", Nosto refetches with only Black SKUs, every family's
 * representative is now Black, and the displayed count jumps from
 * representatives-that-were-Black to families-containing-Black: confusing.
 *
 * Counting across renderable hits *before* dedupe gives a stable answer:
 * "how many families would be visible if this filter were applied". The
 * caller is responsible for passing in renderable hits (post
 * `isRenderableProductHit` filter), so unavailable/handle-less variants don't
 * inflate the count.
 *
 * `normalizeValue` lets the caller collapse different raw encodings of the
 * same logical value into a single key (e.g. color facets receive both
 * `"Black"` and `"Black|#000000"` from Nosto — both should bucket under
 * `"black"`). Defaults to lowercase.
 */
export function countFacetValuesByFamily(
  renderableHits: readonly StyleFamilyHit[],
  field: string | undefined,
  normalizeValue: (value: string) => string = defaultNormalizeValue
): Map<string, number> {
  const counts = new Map<string, number>()
  if (!field) return counts

  const familyValues = new Map<string, Set<string>>()
  for (const hit of renderableHits) {
    const familyKey =
      typeof hit.styleFamilyKey === "string" && hit.styleFamilyKey.length > 0
        ? hit.styleFamilyKey
        : `__product__${hit.productId ?? hit.url ?? ""}`
    let valueSet = familyValues.get(familyKey)
    for (const value of readProductFieldValues(hit, field)) {
      const key = normalizeValue(value)
      if (!key) continue
      if (!valueSet) {
        valueSet = new Set<string>()
        familyValues.set(familyKey, valueSet)
      }
      valueSet.add(key)
    }
  }

  for (const valueSet of familyValues.values()) {
    for (const value of valueSet) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }

  return counts
}

function defaultNormalizeValue(value: string): string {
  return value.toLowerCase()
}
